import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useTheme } from '@mui/material'
import axios, { AxiosRequestConfig } from 'axios'
import { format } from 'date-fns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { getAuthConfig } from '../../utils/authApi'
import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import {
    PMJournalHeaderType,
    PMJournalLineType,
    ListFilter,
    EMPTY_FILTER,
    AI_JOURNAL,
    EMPTY_GUID,
} from './PMJournal.types'
import { getFilteredActivities as filterActivities, getFilteredProjects as filterProjects, violationKey } from './PMJournal.utils'

export const usePMJournal = () => {
    const { t, i18n } = useTranslation()
    const { instance, accounts } = useMsal()
    const [projects, setProjects] = useState<IProject[]>([])
    const theme = useTheme()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [viewMode, setViewMode] = useState<'list' | 'editor'>('list')
    const [isNew, setIsNew] = useState(false)

    // List state
    const [journals, setJournals] = useState<PMJournalHeaderType[]>([])
    const [listFrom, setListFrom] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    )
    const [listTo, setListTo] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
    )
    const [listLoading, setListLoading] = useState(false)
    const [listFilter, setListFilter] = useState<ListFilter>(EMPTY_FILTER)

    // Editor state
    const [journalType, setJournalType] = useState<string>(AI_JOURNAL)
    const [selectedProjectId, setSelectedProjectId] = useState<string>('')
    const [journalDate, setJournalDate] = useState<Date | null>(new Date())
    const [lines, setLines] = useState<PMJournalLineType[]>([])
    const [currentHeader, setCurrentHeader] = useState<PMJournalHeaderType | null>(null)
    const [saving, setSaving] = useState(false)
    const [releasing, setReleasing] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [reopening, setReopening] = useState(false)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null)
    const [permitFrom, setPermitFrom] = useState<Date | null>(null)
    const [permitTo, setPermitTo] = useState<Date | null>(null)
    const [yeWindowFrom, setYeWindowFrom] = useState<Date | null>(null)
    const [yeWindowTo, setYeWindowTo] = useState<Date | null>(null)

    const dateLocale = i18n.language === 'de' ? de : enUS

    useEffect(() => {
        if (dialogOpen) {
            loadPermitFrom()
            loadPMProjects()
        }
    }, [dialogOpen])

    useEffect(() => {
        if (dialogOpen && viewMode === 'list' && listFrom && listTo) loadJournals()
    }, [dialogOpen, listFrom, listTo])

    // ── API helpers ──────────────────────────────────────────────────────────

    const getConfig = async (params?: Record<string, string>): Promise<AxiosRequestConfig | null> => {
        const scope: string = process.env.REACT_APP_SCOPE || ''
        try {
            const baseConfig = await getAuthConfig(instance, accounts[0])
            return { ...baseConfig, params }
        } catch (e) {
            if (e instanceof InteractionRequiredAuthError)
                instance.acquireTokenRedirect({ scopes: [scope], account: accounts[0] })
            return null
        }
    }

    const loadPMProjects = async () => {
        const config = await getConfig()
        if (!config) return
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/PMProjects`, config)
            setProjects(res.data ?? [])
        } catch {
            // fail silently – project dropdown will remain empty
        }
    }

    const loadPermitFrom = async () => {
        const config = await getConfig()
        if (!config) return
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/PMParameters`, config)
            setPermitFrom(res.data?.permitFrom ? new Date(res.data.permitFrom) : null)
            setPermitTo(res.data?.permitTo ? new Date(res.data.permitTo) : null)
            const year = new Date().getFullYear()
            const from: string = res.data?.yeBookingFrom ?? '12-01'
            const to: string = res.data?.yeBookingTo ?? '12-15'
            setYeWindowFrom(new Date(`${year}-${from}`))
            setYeWindowTo(new Date(`${year}-${to}`))
        } catch {
            // fail silently – DatePicker will have no minDate restriction
        }
    }

    // ── Load journal list ────────────────────────────────────────────────────

    const loadJournals = async () => {
        if (!listFrom || !listTo) return
        setListLoading(true)
        setErrorMsg(null)
        const config = await getConfig({
            from: format(listFrom, 'yyyy-MM-dd'),
            to: format(listTo, 'yyyy-MM-dd'),
        })
        if (!config) { setListLoading(false); return }
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, config)
            const data: PMJournalHeaderType[] = res.data ?? []
            data.sort((a, b) => (b.batchnumber ?? '').localeCompare(a.batchnumber ?? ''))
            setJournals(data)
        } catch {
            setErrorMsg(t('pmJournal.loadError'))
        }
        setListLoading(false)
    }

    // ── Open editor ──────────────────────────────────────────────────────────

    const openNewJournal = () => {
        setIsNew(true)
        setJournalType(AI_JOURNAL)
        setSelectedProjectId('')
        setJournalDate(new Date())
        setLines([{ activityNumber: '', hours: 0, delete: false }])
        setCurrentHeader(null)
        setErrorMsg(null)
        setViewMode('editor')
    }

    const openExistingJournal = async (header: PMJournalHeaderType) => {
        setErrorMsg(null)
        const config = await getConfig({ id: header.projJournalTableEntityId! })
        if (!config) return
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, config)
            const data: { header: PMJournalHeaderType; lines: PMJournalLineType[] } = res.data
            setIsNew(false)
            setCurrentHeader(data.header)
            setJournalType(data.header.journalName)
            setSelectedProjectId(data.header.projectId)
            setJournalDate(data.header.date ? new Date(data.header.date) : new Date())
            setLines((data.lines ?? []).filter((l) => !l.delete))
            setViewMode('editor')
        } catch {
            setErrorMsg(t('pmJournal.loadError'))
        }
    }

    // ── Activity filter ──────────────────────────────────────────────────────

    const getFilteredActivities = (projectId: string): IProjectAndActivity[] =>
        filterActivities(journalType, projects, projectId, journalDate)

    const getFilteredProjects = (): IProject[] =>
        filterProjects(projects, journalDate)

    // ── Lines management ─────────────────────────────────────────────────────

    const addLine = () => {
        setLines((prev) => [...prev, { activityNumber: '', hours: 0, delete: false }])
    }

    const updateLine = (idx: number, field: keyof PMJournalLineType, value: any) => {
        setLines((prev) => {
            const copy = [...prev]
            let update: Partial<PMJournalLineType> = { [field]: value }
            // When activity changes: sync resourceId based on journal type
            if (field === 'activityNumber') {
                if (journalType === AI_JOURNAL) {
                    const activity = filterActivities(AI_JOURNAL, projects, selectedProjectId, journalDate)
                        .find((a) => a.mserp_activitynumber === value)
                    update = {
                        ...update,
                        resourceId: activity?.taskResources?.length === 1 ? activity.taskResources[0] : undefined,
                    }
                } else {
                    update = { ...update, resourceId: undefined }
                }
            }
            copy[idx] = { ...copy[idx], ...update }
            return copy
        })
    }

    const removeLine = (idx: number) => {
        setLines((prev) => {
            const copy = [...prev]
            if (copy[idx].projJournalTransEntityId && copy[idx].projJournalTransEntityId !== EMPTY_GUID) {
                copy[idx] = { ...copy[idx], delete: true }
            } else {
                copy.splice(idx, 1)
            }
            return copy
        })
    }

    // ── Violation helper ─────────────────────────────────────────────────────

    const violationMessage = (code: number | undefined, fallback: string): string => {
        const key = violationKey(code)
        return key ? t(key) : fallback
    }

    // ── Save ─────────────────────────────────────────────────────────────────

    const saveJournal = async () => {
        setSaving(true)
        setErrorMsg(null)
        const config = await getConfig()
        if (!config) { setSaving(false); return }
        const payload = {
            header: {
                ...(currentHeader ?? {}),
                journalName: journalType,
                projectId: selectedProjectId,
                date: journalDate ? format(journalDate, "yyyy-MM-dd'T'HH:mm:ss") : null,
            },
            lines,
        }
        try {
            if (isNew || !currentHeader?.projJournalTableEntityId) {
                const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, payload, config)
                setCurrentHeader(res.data.header)
                setLines((res.data.lines ?? []).filter((l: PMJournalLineType) => !l.delete))
                setIsNew(false)
            } else {
                const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, payload, config)
                setLines((res.data.lines ?? []).filter((l: PMJournalLineType) => !l.delete))
            }
            loadJournals()
            setSnackbar({ message: t('pmJournal.saveSuccess'), severity: 'success' })
        } catch (e: any) {
            const msg = violationMessage(e?.response?.data?.VIOLATION_CODE, t('pmJournal.saveError'))
            setErrorMsg(msg)
            setSnackbar({ message: msg, severity: 'error' })
        }
        setSaving(false)
    }

    // ── Release ──────────────────────────────────────────────────────────────

    const releaseJournal = async () => {
        setReleasing(true)
        setErrorMsg(null)
        const config = await getConfig()
        if (!config) { setReleasing(false); return }
        const payload = {
            header: {
                ...(currentHeader ?? {}),
                journalName: journalType,
                projectId: selectedProjectId,
                date: journalDate ? format(journalDate, "yyyy-MM-dd'T'HH:mm:ss") : null,
            },
            lines,
        }
        try {
            let savedHeaderId: string | undefined
            if (isNew || !currentHeader?.projJournalTableEntityId) {
                const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, payload, config)
                setCurrentHeader(res.data.header)
                setLines((res.data.lines ?? []).filter((l: PMJournalLineType) => !l.delete))
                setIsNew(false)
                savedHeaderId = res.data.header?.projJournalTableEntityId
            } else {
                const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, payload, config)
                setLines((res.data.lines ?? []).filter((l: PMJournalLineType) => !l.delete))
                savedHeaderId = currentHeader.projJournalTableEntityId
            }
            if (!savedHeaderId) { setErrorMsg(t('pmJournal.saveError')); setReleasing(false); return }
            const releaseConfig = await getConfig({ id: savedHeaderId })
            if (!releaseConfig) { setReleasing(false); return }
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/PMReleaseJournal`, {}, releaseConfig)
            setCurrentHeader(prev => prev ? { ...prev, posted: true } : null)
            loadJournals()
            setSnackbar({ message: t('pmJournal.releaseSuccess'), severity: 'success' })
        } catch (e: any) {
            const msg = violationMessage(e?.response?.data?.VIOLATION_CODE, t('pmJournal.releaseError'))
            setErrorMsg(msg)
            setSnackbar({ message: msg, severity: 'error' })
        }
        setReleasing(false)
    }

    // ── Reopen ───────────────────────────────────────────────────────────────

    const reopenJournal = async () => {
        if (!currentHeader?.projJournalTableEntityId) return
        setReopening(true)
        setErrorMsg(null)
        const config = await getConfig({ id: currentHeader.projJournalTableEntityId })
        if (!config) { setReopening(false); return }
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/PMReopenJournal`, {}, config)
            setCurrentHeader({ ...currentHeader, posted: false })
            loadJournals()
            setSnackbar({ message: t('pmJournal.reopenSuccess'), severity: 'success' })
        } catch (e: any) {
            const msg = violationMessage(e?.response?.data?.VIOLATION_CODE, t('pmJournal.reopenError'))
            setErrorMsg(msg)
            setSnackbar({ message: msg, severity: 'error' })
        }
        setReopening(false)
    }

    // ── Delete ───────────────────────────────────────────────────────────────

    const deleteJournal = async () => {
        if (!currentHeader?.projJournalTableEntityId) return
        setDeleting(true)
        setErrorMsg(null)
        const config = await getConfig({ id: currentHeader.projJournalTableEntityId })
        if (!config) { setDeleting(false); return }
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/PMJournal`, config)
            setViewMode('list')
            loadJournals()
            setSnackbar({ message: t('pmJournal.deleteSuccess'), severity: 'success' })
        } catch (e: any) {
            const msg = e?.response?.data?.VIOLATION_CODE ?? t('pmJournal.deleteError')
            setErrorMsg(msg)
            setSnackbar({ message: msg, severity: 'error' })
        }
        setDeleting(false)
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    const projectName = (id: string) => {
        const p = projects.find((pr) => pr.mserp_projectid === id)
        return p ? `${p.mserp_projectid} – ${p.mserp_projectname}` : id
    }

    const isBusy = saving || releasing || deleting || reopening

    const isWithinYeWindow = (() => {
        if (!yeWindowFrom || !yeWindowTo) return false
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today >= yeWindowFrom && today <= yeWindowTo
    })()

    return {
        // Dialog control
        dialogOpen, setDialogOpen,
        viewMode, setViewMode,
        isNew,
        // List
        journals, listLoading,
        listFrom, setListFrom,
        listTo, setListTo,
        listFilter, setListFilter,
        errorMsg, setErrorMsg,
        theme,
        // Editor
        journalType, setJournalType,
        selectedProjectId, setSelectedProjectId,
        journalDate, setJournalDate,
        lines, setLines,
        currentHeader,
        saving, releasing, deleting, reopening, isBusy,
        snackbar, setSnackbar,
        permitFrom,
        permitTo,
        isWithinYeWindow,
        yeWindowFrom,
        yeWindowTo,
        dateLocale,
        // Shared data
        projects,
        // Actions
        openNewJournal, openExistingJournal,
        getFilteredActivities,
        getFilteredProjects,
        addLine, updateLine, removeLine,
        saveJournal, releaseJournal, reopenJournal, deleteJournal,
        projectName,
    }
}
